import mqtt, { MqttClient, IClientOptions } from 'mqtt';
import { MQTTEvent } from '../types/event';

export interface MQTTConfig {
  brokerUrl: string;
  clientId: string;
  username: string;
  password: string;
}

type MessageHandler = (event: MQTTEvent) => void;
type GenericHandler = (topic: string, event: MQTTEvent) => void;

const STORAGE_KEY_HEARTBEAT = 'nre_heartbeat_interval';
const TOPIC_PREFIX = import.meta.env.VITE_MQTT_TOPIC_PREFIX || 'nre';

class MQTTClientWrapper {
  private client: MqttClient | null = null;
  private topicHandlers: Map<string, Set<MessageHandler>> = new Map();
  private genericHandlers: Set<GenericHandler> = new Set();
  private connected = false;
  private connecting = false;
  private isIntentionallyClosed = false;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private config: MQTTConfig | null = null;
  private _userId: string | null = null;

  get userId(): string | null {
    return this._userId;
  }

  connect(config: MQTTConfig): void {
    // Already connected or connecting
    if (this.connected || this.connecting) {
      console.log('[MQTT] Already connected or connecting');
      return;
    }

    this.isIntentionallyClosed = false;
    this.connecting = true;
    this.config = config;
    this._userId = config.username;

    const options: IClientOptions = {
      clientId: config.clientId,
      username: config.username,
      password: config.password,
      clean: false, // Persistent session - receive offline messages
      reconnectPeriod: 0, // We handle reconnection manually
      connectTimeout: 10000,
    };

    console.log('[MQTT] Connecting to:', config.brokerUrl);
    this.client = mqtt.connect(config.brokerUrl, options);

    this.client.on('connect', () => {
      console.log('[MQTT] Connected');
      this.connected = true;
      this.connecting = false;
      this.reconnectAttempts = 0;
      
      // Resubscribe to all topics
      this.resubscribeAll();
      
      // Start heartbeat
      this.startHeartbeat();
    });

    this.client.on('error', (err) => {
      console.error('[MQTT] Error:', err);
    });

    this.client.on('close', () => {
      console.log('[MQTT] Connection closed');
      this.connected = false;
      this.connecting = false;
      this.stopHeartbeat();
      
      if (!this.isIntentionallyClosed) {
        this.scheduleReconnect();
      }
    });

    this.client.on('message', (topic, payload) => {
      try {
        const event = JSON.parse(payload.toString()) as MQTTEvent;
        
        // Notify topic-specific handlers
        const handlers = this.topicHandlers.get(topic);
        if (handlers) {
          handlers.forEach((handler) => handler(event));
        }
        
        // Notify generic handlers
        this.genericHandlers.forEach((handler) => handler(topic, event));
      } catch (err) {
        console.error('[MQTT] Failed to parse message:', err);
      }
    });
  }

  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.stopHeartbeat();
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.client) {
      this.client.end();
      this.client = null;
      this.connected = false;
      this.connecting = false;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout || this.isIntentionallyClosed) {
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`[MQTT] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (this.config) {
        this.connecting = false; // Reset to allow reconnect
        this.connect(this.config);
      }
    }, delay);
  }

  private resubscribeAll(): void {
    this.topicHandlers.forEach((_, topic) => {
      this.client?.subscribe(topic, { qos: 1 }, (err) => {
        if (err) {
          console.error(`[MQTT] Failed to resubscribe to ${topic}:`, err);
        } else {
          console.log(`[MQTT] Resubscribed to ${topic}`);
        }
      });
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    const intervalSec = parseInt(localStorage.getItem(STORAGE_KEY_HEARTBEAT) || '30', 10);
    const intervalMs = intervalSec * 1000;
    
    console.log('[MQTT] Starting heartbeat, interval:', intervalSec, 's');
    
    // Send immediately
    this.sendHeartbeat();
    
    // Then on interval
    this.heartbeatInterval = setInterval(() => this.sendHeartbeat(), intervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private sendHeartbeat(): void {
    if (!this.connected || !this.config || !this._userId) return;
    
    const topic = `${TOPIC_PREFIX}/users/${this._userId}/heartbeat`;
    this.publish(topic, {
      type: 'heartbeat',
      timestamp: new Date().toISOString(),
      payload: { 
        user_id: this._userId,
        client_type: 'miniapp',
      },
    });
  }

  subscribe(topic: string, handler: MessageHandler): () => void {
    if (!this.topicHandlers.has(topic)) {
      this.topicHandlers.set(topic, new Set());
      
      // Subscribe if connected
      if (this.connected && this.client) {
        this.client.subscribe(topic, { qos: 1 }, (err) => {
          if (err) {
            console.error(`[MQTT] Failed to subscribe to ${topic}:`, err);
          } else {
            console.log(`[MQTT] Subscribed to ${topic}`);
          }
        });
      }
    }

    this.topicHandlers.get(topic)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.topicHandlers.get(topic);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.topicHandlers.delete(topic);
          this.client?.unsubscribe(topic);
        }
      }
    };
  }

  // Subscribe to all messages (generic handler)
  onMessage(handler: GenericHandler): () => void {
    this.genericHandlers.add(handler);
    return () => {
      this.genericHandlers.delete(handler);
    };
  }

  publish(topic: string, payload: unknown): void {
    if (!this.client || !this.connected) {
      console.warn('[MQTT] Not connected, cannot publish');
      return;
    }

    this.client.publish(topic, JSON.stringify(payload), { qos: 1 });
  }

  // Sync command - publish and wait for response
  async command<T = unknown>(
    commandTopic: string,
    responseTopic: string,
    payload: unknown,
    timeoutMs = 5000
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        unsubscribe();
        reject(new Error('Command timeout'));
      }, timeoutMs);

      const unsubscribe = this.subscribe(responseTopic, (event) => {
        clearTimeout(timeout);
        unsubscribe();
        resolve(event.payload as T);
      });

      this.publish(commandTopic, payload);
    });
  }

  isConnected(): boolean {
    return this.connected;
  }

  // Topic builders
  static topics = {
    userEvents: (userId: string) => `${TOPIC_PREFIX}/users/${userId}/events`,
    groupEvents: (groupId: string) => `${TOPIC_PREFIX}/groups/${groupId}/events`,
    broadcast: (eventType: string) => `${TOPIC_PREFIX}/broadcast/${eventType}`,
    userHeartbeat: (userId: string) => `${TOPIC_PREFIX}/users/${userId}/heartbeat`,
    pluginEvents: (pluginId: string, eventType: string) => `plugins/${pluginId}/events/${eventType}`,
    systemMonitoring: () => `${TOPIC_PREFIX}/system/monitoring/metrics`,
  };
}

// Singleton instance
export const mqttClient = new MQTTClientWrapper();
export const TOPICS = MQTTClientWrapper.topics;
